import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Box,
  CircularProgress,
  Grid,
  LinearProgress,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Upload as UploadIcon,
} from "@mui/icons-material";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { videosAPI, adminAPI } from "../../services/api";
import { useTranslation } from "react-i18next";
import CustomContainer from "../../components/common/CustomContainer";

const VideosManagement = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    duration: "",
  });
  const [videoFile, setVideoFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [maxVideoFileSize, setMaxVideoFileSize] = useState(500);
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await adminAPI.getSettings();
        if (response.data.maxVideoFileSize) {
          setMaxVideoFileSize(parseInt(response.data.maxVideoFileSize));
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
        showSnackbar("Error loading file size limits", "error");
      }
    };

    fetchSettings();
  }, [showSnackbar]);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const response = await videosAPI.getAll();
      setVideos(response.data);
    } catch (error) {
      showSnackbar(t("videos.messages.loadError"), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  const handleOpenDialog = (video = null) => {
    if (video) {
      setSelectedVideo(video);
      setFormData({
        title: video.title,
        description: video.description,
        duration: video.duration || "",
      });
    } else {
      setSelectedVideo(null);
      setFormData({
        title: "",
        description: "",
        duration: "",
      });
    }
    setVideoFile(null);
    setThumbnailFile(null);
    setUploadProgress(0);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedVideo(null);
    setFormData({
      title: "",
      description: "",
      duration: "",
    });
    setVideoFile(null);
    setThumbnailFile(null);
    setUploadProgress(0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Validate required fields
      if (!formData.title.trim()) {
        showSnackbar("Title is required", "error");
        return;
      }

      if (!videoFile && !selectedVideo) {
        showSnackbar("Video file is required", "error");
        return;
      }

      const formDataToSend = new FormData();

      // Add basic form fields
      formDataToSend.append("title", formData.title.trim());
      formDataToSend.append("description", formData.description.trim());
      if (formData.duration) {
        formDataToSend.append("duration", formData.duration.toString());
      }

      // Add files only if they exist
      if (videoFile) {
        formDataToSend.append("video", videoFile);
      }
      if (thumbnailFile) {
        formDataToSend.append("thumbnail", thumbnailFile);
      }

      // Log the form data for debugging
      console.log("Form data being sent:");
      for (let pair of formDataToSend.entries()) {
        console.log(
          pair[0] + ": " + (pair[1] instanceof File ? pair[1].name : pair[1])
        );
      }

      let response;
      if (selectedVideo) {
        response = await videosAPI.update(
          selectedVideo.id,
          formDataToSend,
          setUploadProgress
        );
      } else {
        response = await videosAPI.create(formDataToSend, setUploadProgress);
      }

      showSnackbar(
        t(
          selectedVideo
            ? "videos.messages.updateSuccess"
            : "videos.messages.createSuccess"
        ),
        "success"
      );

      handleCloseDialog();
      fetchVideos();
    } catch (error) {
      console.error("Video operation error:", error);
      // Log more detailed error information
      if (error.response) {
        console.error("Error response data:", error.response.data);
        console.error("Error response status:", error.response.status);
        console.error("Error response headers:", error.response.headers);

        // Show more specific error message
        const errorMessage =
          error.response.data?.message ||
          t(
            selectedVideo
              ? "videos.messages.updateError"
              : "videos.messages.createError"
          );
        showSnackbar(errorMessage, "error");
      } else if (error.request) {
        console.error("Error request:", error.request);
        showSnackbar("Network error occurred", "error");
      } else {
        console.error("Error message:", error.message);
        showSnackbar(error.message, "error");
      }
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t("videos.messages.deleteConfirm"))) {
      try {
        await videosAPI.delete(id);
        showSnackbar(t("videos.messages.deleteSuccess"), "success");
        fetchVideos();
      } catch (error) {
        showSnackbar(
          error.response?.data?.message || t("videos.messages.deleteError"),
          "error"
        );
      }
    }
  };

  const handleVideoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("video/")) {
        showSnackbar("Please upload a valid video file", "error");
        return;
      }

      // Validate file size
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > maxVideoFileSize) {
        showSnackbar(
          `Video file size must be less than ${maxVideoFileSize}MB`,
          "error"
        );
        return;
      }

      setVideoFile(file);

      // Get video duration
      const video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () => {
        setFormData((prev) => ({
          ...prev,
          duration: Math.round(video.duration),
        }));
      };
      video.src = URL.createObjectURL(file);
    }
  };

  const handleThumbnailChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        showSnackbar("Please upload a valid image file", "error");
        return;
      }

      // Validate file size (e.g., 5MB max)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        showSnackbar("Thumbnail file size must be less than 5MB", "error");
        return;
      }

      setThumbnailFile(file);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" m={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <CustomContainer>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h5" component="h2">
            {t("videos.management")}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            {t("videos.add")}
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("videos.form.title")}</TableCell>
                <TableCell>{t("videos.form.description")}</TableCell>
                <TableCell>{t("videos.details.duration")}</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {videos.map((video) => (
                <TableRow key={video.id}>
                  <TableCell>{video.title}</TableCell>
                  <TableCell>{video.description}</TableCell>
                  <TableCell>
                    {Math.floor(video.duration / 60)}:
                    {String(video.duration % 60).padStart(2, "0")}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenDialog(video)}
                      title={t("videos.edit")}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(video.id)}
                      title={t("videos.delete")}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedVideo ? t("videos.edit") : t("videos.add")}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("videos.form.title")}
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  margin="normal"
                  required
                  error={!formData.title}
                  helperText={!formData.title && t("videos.form.titleRequired")}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("videos.form.description")}
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  margin="normal"
                  multiline
                  rows={4}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("videos.form.duration")}
                  type="number"
                  value={formData.duration}
                  onChange={(e) =>
                    setFormData({ ...formData, duration: e.target.value })
                  }
                  margin="normal"
                  required
                  error={!formData.duration}
                  helperText={
                    !formData.duration && t("videos.form.durationRequired")
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <input
                  accept="video/*"
                  style={{ display: "none" }}
                  id="video-file"
                  type="file"
                  onChange={handleVideoChange}
                />
                <label htmlFor="video-file">
                  <Button
                    variant="contained"
                    component="span"
                    startIcon={<UploadIcon />}
                    fullWidth
                  >
                    {videoFile ? videoFile.name : t("videos.form.uploadVideo")}
                  </Button>
                </label>
              </Grid>
              <Grid item xs={12}>
                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id="thumbnail-file"
                  type="file"
                  onChange={handleThumbnailChange}
                />
                <label htmlFor="thumbnail-file">
                  <Button
                    variant="contained"
                    component="span"
                    startIcon={<UploadIcon />}
                    fullWidth
                  >
                    {thumbnailFile
                      ? thumbnailFile.name
                      : t("videos.form.uploadThumbnail")}
                  </Button>
                </label>
              </Grid>
              {isUploading && (
                <Grid item xs={12}>
                  <Box sx={{ width: "100%", mt: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box sx={{ width: "100%", mr: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={uploadProgress}
                        />
                      </Box>
                      <Box sx={{ minWidth: 35 }}>
                        <Typography variant="body2" color="text.secondary">
                          {`${Math.round(uploadProgress)}%`}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: "block" }}
                    >
                      {t("common.upload.uploading")}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              {selectedVideo ? t("videos.edit") : t("videos.add")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </CustomContainer>
  );
};

export default VideosManagement;
